/**
 * Versioned API Utility for HHAX Compliance
 * 
 * This utility provides a consistent way to make versioned API calls
 * across the entire frontend application, ensuring HHAX compliance
 * and proper API version headers.
 */

import axios from 'axios';
import { useMemo } from 'react';
import { API_BASE_URL } from '../config/api';
import { captureTelemetryException } from '../config/telemetryLoader';

import { safeStorage } from './safeStorage';
import { authRedirect } from './authRedirect';
import { clearAuthData } from './tokenUtils';
import { withClinicalDraftPersistence } from './clinicalDraftApi';

// Request timeout / retry policy.
// Default 30s covers cold starts + most slow queries; callers can override
// per-request (e.g. PDF or AI calls) by passing { timeout: N }.
const DEFAULT_TIMEOUT_MS = 30000;
const MAX_RETRIES = 2; // → 3 attempts total
const RETRY_BACKOFF_MS = [1000, 3000, 9000];
const AUTH_REFRESH_THRESHOLD_SECONDS = 10 * 60;

// Only retry idempotent methods. Retrying POST/PUT/DELETE risks double-mutations.
const RETRIABLE_METHODS = new Set(['get', 'head', 'options']);
const UUID_PATH_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?=\/|\?|$)/gi;

const completionPayloadFingerprint = (payload) => {
  const stablePayload = JSON.stringify(payload, (key, value) =>
    key === 'completedAt' ? undefined : value
  );
  let hash = 2166136261;
  for (let index = 0; index < stablePayload.length; index += 1) {
    hash ^= stablePayload.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const clinicalCompletionIdempotencyKey = (url, payload) => {
  if (!String(url || '').endsWith('/clinical/universal/complete')) return null;
  const draftId = payload?.draftId || payload?.metadata?.draftId;
  if (!/^[a-fA-F0-9-]{8,128}$/.test(String(draftId || ''))) return null;
  return `${draftId}-${completionPayloadFingerprint(payload)}`;
};

// Telemetry needs the route shape, not record identifiers or query values.
// Keep parameter names for diagnosis while removing UUID path segments and all
// query values before constructing Sentry issue titles/extras.
export const sanitizeRequestUrlForTelemetry = (rawUrl = '') => {
  const [rawPath, rawQuery = ''] = String(rawUrl).split('?');
  const path = rawPath.replace(UUID_PATH_SEGMENT, '/:id');
  if (!rawQuery) return path;
  const keys = [...new Set(new URLSearchParams(rawQuery).keys())].sort();
  return keys.length > 0
    ? `${path}?${keys.map((key) => `${encodeURIComponent(key)}=[redacted]`).join('&')}`
    : path;
};

let authRefreshPromise = null;

const getJwtTiming = (token) => {
  if (!token) return { exists: false, expired: true, timeUntilExpiry: 0 };
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return { exists: true, expired: true, timeUntilExpiry: 0 };
    const timeUntilExpiry = Math.floor((payload.exp * 1000 - Date.now()) / 1000);
    return {
      exists: true,
      expired: timeUntilExpiry <= 0,
      timeUntilExpiry,
    };
  } catch {
    return { exists: true, expired: true, timeUntilExpiry: 0 };
  }
};

// A transient failure (timeout, network drop, or upstream 5xx) is NOT an auth
// rejection — under DB pressure the refresh endpoint is just slow. Retrying a few
// times prevents a single slow moment from ending the session (and force-logging
// the user out). A real 401/403 means the refresh token itself is invalid → stop.
const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_BACKOFF_MS = [500, 1500];
const isCanceledRequest = (error) =>
  axios.isCancel(error) ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError';

const isTransientRefreshError = (error) => {
  if (isCanceledRequest(error)) return false;
  if (error.code === 'ECONNABORTED') return true; // timeout
  if (!error.response) return true;                // network / no response
  const s = error.response.status;
  return s === 500 || s === 502 || s === 503 || s === 504;
};

const performTokenRefresh = async (currentToken) => {
  let lastError = null;
  for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt++) {
    try {
      const response = await axios.post(`${API_BASE_URL}/v1/users/refresh-token`, {}, {
        withCredentials: true,
        timeout: DEFAULT_TIMEOUT_MS,
        headers: {
          'Content-Type': 'application/json',
          'API-Version': 'v1',
          'Accept': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
      });
      const newToken = response.data?.token;
      if (newToken) {
        safeStorage.setItem('evv_token', newToken);
        return newToken;
      }
      return null; // 200 but no token — definitive failure, don't retry
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      // Real auth rejection — the refresh credential is invalid. The session is
      // genuinely over; stop retrying so the 401 handler can log out cleanly.
      if (status === 401 || status === 403) return null;
      // Transient — back off and try again before giving up.
      if (isTransientRefreshError(error) && attempt < REFRESH_MAX_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, REFRESH_BACKOFF_MS[attempt] || 1500));
        continue;
      }
      break;
    }
  }
  console.warn('Token refresh failed:', lastError?.response?.data || lastError?.message);
  return null;
};

export const refreshStoredAuthToken = async (currentToken) => {
  if (!currentToken) return null;

  // Deduplicate concurrent refreshes — one in-flight refresh serves all callers.
  if (!authRefreshPromise) {
    authRefreshPromise = performTokenRefresh(currentToken)
      .finally(() => {
        authRefreshPromise = null;
      });
  }

  return authRefreshPromise;
};

// The redirect is a full page load, so clear everything AuthContext restores a
// session from (including evv_user_cache), not just the token.
const endExpiredClientSession = () => {
  clearAuthData();
  authRedirect('/login', 'session_expired');
};

const isRetriableError = (error) => {
  // A caller-owned AbortSignal means the page/component no longer wants this
  // request. Retrying an already-aborted request only creates more canceled
  // calls and false RetryExhausted alerts.
  if (isCanceledRequest(error)) return false;
  // Timeout
  if (error.code === 'ECONNABORTED') return true;
  // Network / connection failure (no response received)
  if (!error.response) return true;
  // Transient upstream errors
  const status = error.response.status;
  if (status === 502 || status === 503 || status === 504) return true;
  // 500 with our backend's SERVER_ERROR envelope: Prisma "Connection is closed"
  // and similar transient failures surface here. Combined with the
  // RETRIABLE_METHODS gate below, only idempotent reads retry — POST/PUT/DELETE
  // never double-fire. See 2026-05-11 incident: a poisoned Prisma pool on one
  // pod served 500s mid-workday and the frontend gave up after one try.
  if (status === 500 && error.response.data?.code === 'SERVER_ERROR') return true;
  return false;
};

/**
 * Request Deduplication Cache
 * Prevents identical, concurrent GET requests from issuing duplicate network calls.
 * Returns the same promise for identical requests
 */
const inFlightRequests = new Map();

const getRequestContextKey = () => {
  let userId = 'anonymous';
  let organizationId = safeStorage.getItem('dev_organization') || 'host';

  try {
    const user = JSON.parse(safeStorage.getItem('evv_user') || '{}');
    userId = user.id || user.userId || userId;
    organizationId = user.organizationId || organizationId;
  } catch {
    // Malformed legacy storage must not break API requests.
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'server';
  return `${hostname}:${organizationId}:${userId}`;
};

const getRequestKey = (method, url, config) => {
  // Tenant/user context prevents a request started under one authenticated
  // session from ever satisfying another session after an account switch.
  const representation = JSON.stringify({
    params: config?.params || null,
    responseType: config?.responseType || 'json',
    accept: config?.headers?.Accept || config?.headers?.accept || null,
    range: config?.headers?.Range || config?.headers?.range || null,
  });
  return `${getRequestContextKey()}:${method}:${url}:${representation}`;
};

const deduplicateRequest = (key, requestFn) => {
  // Check if there's already an in-flight request for this key
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  // Create the request promise
  const requestPromise = requestFn().finally(() => {
    // Do not retain completed responses. A read immediately following a
    // mutation must always reach the backend for fresh tenant data.
    inFlightRequests.delete(key);
  });

  // Store the promise
  inFlightRequests.set(key, requestPromise);
  return requestPromise;
};

/**
 * Create a versioned API client with automatic version headers
 * @param {string} version - API version (e.g., 'v1', 'v2')
 * @param {object} defaultConfig - Default axios configuration
 * @returns {object} API client with versioned methods
 */
export const createVersionedApi = (version = 'v1', defaultConfig = {}) => {
  // Ensure version has 'v' prefix and handle null/undefined
  const versionString = (version && typeof version === 'string' && version.startsWith('v')) ? version : `v${version || '1'}`;
  
  const baseConfig = {
    baseURL: API_BASE_URL,
    withCredentials: true, // Send httpOnly cookie automatically (primary auth for web)
    timeout: DEFAULT_TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
      'API-Version': versionString,
      'Accept': 'application/json',
      // Cache-busting headers to prevent browser cache issues
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      ...defaultConfig.headers
    },
    ...defaultConfig
  };

  // Create the axios instance
  const apiClient = axios.create(baseConfig);

  // Add request interceptor to dynamically include auth token
  apiClient.interceptors.request.use(async (config) => {
    // Axios serializes FormData to JSON when an application/json header is
    // present. That preserves ordinary text fields but turns File values into
    // empty JSON objects, so multer never receives req.file. Remove any
    // inherited or caller-supplied content type and let the browser generate
    // multipart/form-data with the required boundary.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    // Clinical completion can take longer than the UI's feedback loop. If a
    // clinician retries the same saved draft after a lost/slow response, give
    // the server a stable key for the exact payload so it replays the first
    // result instead of creating a second completed document. The payload hash
    // intentionally excludes completedAt, which is transport metadata and may
    // change between otherwise identical retries.
    const completionKey = clinicalCompletionIdempotencyKey(config.url, config.data);
    if (completionKey && !config.headers?.get?.('Idempotency-Key') && !config.headers?.['Idempotency-Key']) {
      if (typeof config.headers?.set === 'function') {
        config.headers.set('Idempotency-Key', completionKey);
      } else {
        config.headers = { ...(config.headers || {}), 'Idempotency-Key': completionKey };
      }
    }

    let token = safeStorage.getItem('evv_token');
    const endpoint = config.url || '';
    const shouldRefresh =
      token &&
      !endpoint.includes('/users/login') &&
      !endpoint.includes('/users/refresh-token');

    if (shouldRefresh) {
      const timing = getJwtTiming(token);
      if (timing.expired) {
        // Token is already expired — must refresh before sending or the request
        // will 401. Block on it (deduped across concurrent requests).
        const refreshedToken = await refreshStoredAuthToken(token);
        if (refreshedToken) {
          token = refreshedToken;
        } else {
          // Do not dispatch the original request with a token we already know
          // is expired. That used to create a burst of 401s, component error
          // banners, and misleading RetryExhausted Sentry events while the
          // login redirect was already underway. A cancellation is intentional
          // control flow and is ignored by the retry/telemetry layers.
          endExpiredClientSession();
          throw new axios.CanceledError('Session expired; sign-in required');
        }
      } else if (timing.timeUntilExpiry <= AUTH_REFRESH_THRESHOLD_SECONDS) {
        // Still valid but nearing expiry: refresh in the BACKGROUND so this
        // request isn't stalled behind a slow refresh when the DB is busy. The
        // next request picks up the new token; this one uses the still-valid one.
        refreshStoredAuthToken(token);
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Retry interceptor — handles transient failures (timeout, network drop,
  // 502/503/504) with exponential backoff. Idempotent methods only.
  // On final exhaustion, tags the error with `isRetryExhausted` so Sentry
  // captures it (bypassing the default network-error filter).
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      if (!config) return Promise.reject(error);

      // Cancellation is a normal component-lifecycle outcome, not an outage.
      // Return before retry accounting and Sentry reporting.
      if (isCanceledRequest(error)) return Promise.reject(error);

      const method = (config.method || 'get').toLowerCase();
      const retriesSoFar = config.__retryCount || 0;

      // Some callers deliberately implement their own immediate fallback (for
      // example, the ICD lookup moves from UMLS to WHO). Retrying the first
      // provider delays that fallback and creates a false RetryExhausted event.
      if (config.retry === false || !RETRIABLE_METHODS.has(method) || !isRetriableError(error) || retriesSoFar >= MAX_RETRIES) {
        if (retriesSoFar > 0) {
          error.isRetryExhausted = true;
          error.retryAttempts = retriesSoFar + 1;
          try {
            // Wrap with a distinctive message so Sentry's ignoreErrors
            // (which text-matches "Network Error" / "Failed to fetch")
            // doesn't drop the event before beforeSend runs.
            const originalMsg = error.message || error.code || 'request failed';
            const telemetryUrl = sanitizeRequestUrlForTelemetry(config.url);
            const wrapped = new Error(
              `RetryExhausted: ${method.toUpperCase()} ${telemetryUrl} failed after ${retriesSoFar + 1} attempts (${originalMsg})`
            );
            wrapped.isRetryExhausted = true;
            wrapped.originalError = error;
            void captureTelemetryException(wrapped, {
              tags: { retryExhausted: true, requestMethod: method },
              extra: {
                url: telemetryUrl,
                attempts: retriesSoFar + 1,
                code: error.code || null,
                status: error.response?.status || null,
              },
            });
          } catch {
            // Sentry not initialized (dev/test) — ignore
          }
        }
        return Promise.reject(error);
      }

      config.__retryCount = retriesSoFar + 1;
      const delay = RETRY_BACKOFF_MS[retriesSoFar] || RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];
      await new Promise((resolve) => setTimeout(resolve, delay));
      return apiClient.request(config);
    }
  );

  // Add response interceptor for bundle access errors
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Auto-clear corrupted/expired tokens on 401 to prevent redirect loops
      if (error.response?.status === 401) {
        const code = error.response?.data?.code;
        const config = error.config;

        // A TOKEN_EXPIRED/INVALID_TOKEN 401 is often just a stale token that raced
        // a slow refresh (e.g. DB under pressure), NOT a truly dead session. Before
        // logging out, attempt one fresh refresh-and-retry. If the refresh succeeds
        // the original request replays transparently and the user stays logged in.
        //
        // TOKEN_VERSION_MISMATCH is also recoverable: it almost always means an admin
        // bumped the user's token version mid-session (a role/permission change), not
        // a real revocation. The refresh endpoint mints a new JWT carrying the current
        // version (and re-checks `disabled`), so a refresh-and-retry keeps the user
        // working through a benign bump. A genuinely revoked/disabled account fails the
        // refresh (403) and falls through to the logout below.
        // ACCOUNT_DISABLED and SESSION_REVOKED (this session was signed out,
        // possibly from another tab) are never recoverable → skip straight to logout.
        const recoverable = code === 'TOKEN_EXPIRED' || code === 'INVALID_TOKEN' || code === 'TOKEN_VERSION_MISMATCH';
        if (recoverable && config && !config.__authRetried) {
          config.__authRetried = true;
          const refreshed = await refreshStoredAuthToken(safeStorage.getItem('evv_token'));
          if (refreshed) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${refreshed}`;
            return apiClient.request(config);
          }
        }

        if (code === 'INVALID_TOKEN' || code === 'TOKEN_EXPIRED' || code === 'ACCOUNT_DISABLED' || code === 'TOKEN_VERSION_MISMATCH' || code === 'SESSION_REVOKED') {
          // Redirect to login using centralized guard to prevent redirect storms.
          endExpiredClientSession();
        }
      }
      if (error.response?.status === 403 && error.response?.data?.bundlesRequired) {
        const bundles = error.response.data.bundlesRequired;
        const bundleNames = bundles.map(s => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())).join(', ');
        console.warn(`Bundle access denied. Required: ${bundleNames}`);
        error.bundleError = true;
        error.bundleMessage = `This feature requires the ${bundleNames} bundle. Contact your administrator to upgrade.`;
      }
      return Promise.reject(error);
    }
  );

  return {
    /**
     * GET request with versioned endpoint
     * Includes request deduplication to prevent duplicate calls
     */
    get: (endpoint, config = {}) => {
      const fullPath = `/${versionString}${endpoint}`;

      const requestKey = getRequestKey('GET', fullPath, config);

      const makeRequest = () => apiClient.get(fullPath, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          ...config.headers
        }
      });

      // AbortSignals and legacy cancel tokens are owned by one caller. Sharing
      // such a promise would let one component cancel another component's
      // otherwise identical request.
      if (config.signal || config.cancelToken) return makeRequest();

      return deduplicateRequest(requestKey, makeRequest);
    },

    /**
     * POST request with versioned endpoint
     */
    post: (endpoint, data, config = {}) => {
      return apiClient.post(`/${versionString}${endpoint}`, data, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          ...config.headers
        }
      });
    },

    /**
     * PUT request with versioned endpoint
     */
    put: (endpoint, data, config = {}) => {
      return apiClient.put(`/${versionString}${endpoint}`, data, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          ...config.headers
        }
      });
    },

    /**
     * PATCH request with versioned endpoint
     */
    patch: (endpoint, data, config = {}) => {
      return apiClient.patch(`/${versionString}${endpoint}`, data, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          ...config.headers
        }
      });
    },

    /**
     * DELETE request with versioned endpoint
     */
    delete: (endpoint, config = {}) => {
      return apiClient.delete(`/${versionString}${endpoint}`, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          ...config.headers
        }
      });
    }
  };
};

/**
 * Default API client using version 1 (current implementation)
 */
export const apiV1 = createVersionedApi('v1');

/**
 * API client for future HHAX enhanced features (version 2)
 */
export const apiV2 = createVersionedApi('v2');

/**
 * Authenticated API client factory
 * Automatically includes Authorization header from localStorage
 * @param {string} version - API version
 * @returns {object} Authenticated API client
 */
export const createAuthenticatedApi = (version = 'v1') => {
  // The request interceptor will handle token dynamically
  return createVersionedApi(version);
};

/**
 * Higher-order function to create API methods with automatic auth
 * @param {string} version - API version
 * @returns {object} API methods with automatic authentication
 */
export const useVersionedApi = (version = 'v1') => {
  // Use useMemo to prevent creating new API client instance on every render
  const apiClient = useMemo(() => {
    return withClinicalDraftPersistence(createAuthenticatedApi(version));
  }, [version]); // Only recreate if version changes
  
  return apiClient;
};

/**
 * Legacy API support - fallback to non-versioned endpoints
 * This should only be used during migration period
 */
export const createLegacyApi = (defaultConfig = {}) => {
  const token = safeStorage.getItem('evv_token');

  const baseConfig = {
    baseURL: API_BASE_URL,
    withCredentials: true, // Send httpOnly cookie automatically
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...defaultConfig.headers
    },
    ...defaultConfig
  };

  // Create the axios instance for legacy API
  const apiClient = axios.create(baseConfig);

  return {
    get: (endpoint, config = {}) => {
      return apiClient.get(`/v1${endpoint}`, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          'API-Version': 'v1',
          ...config.headers
        }
      });
    },
    post: (endpoint, data, config = {}) => {
      return apiClient.post(`/v1${endpoint}`, data, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          'API-Version': 'v1',
          ...config.headers
        }
      });
    },
    put: (endpoint, data, config = {}) => {
      return apiClient.put(`/v1${endpoint}`, data, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          'API-Version': 'v1',
          ...config.headers
        }
      });
    },
    patch: (endpoint, data, config = {}) => {
      return apiClient.patch(`/v1${endpoint}`, data, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          'API-Version': 'v1',
          ...config.headers
        }
      });
    },
    delete: (endpoint, config = {}) => {
      return apiClient.delete(`/v1${endpoint}`, {
        ...baseConfig,
        ...config,
        headers: {
          ...baseConfig.headers,
          'API-Version': 'v1',
          ...config.headers
        }
      });
    }
  };
};

/**
 * Migration helper - dual API support
 * Tries versioned API first, falls back to legacy if needed
 * @param {string} version - Preferred API version
 * @returns {object} API client with fallback support
 */
export const createMigrationApi = (version = 'v1') => {
  const versionedApi = createAuthenticatedApi(version);
  const legacyApi = createLegacyApi();

  const makeRequestWithFallback = async (method, endpoint, ...args) => {
    try {
      // Try versioned API first
      return await versionedApi[method](endpoint, ...args);
    } catch (error) {
      // If versioned API fails with 404/405, try legacy
      if (error.response?.status === 404 || error.response?.status === 405) {
        console.warn(`Versioned API failed for ${method.toUpperCase()} ${endpoint}, falling back to legacy`);
        return await legacyApi[method](endpoint, ...args);
      }
      // Re-throw other errors
      throw error;
    }
  };

  return {
    get: (endpoint, config) => makeRequestWithFallback('get', endpoint, config),
    post: (endpoint, data, config) => makeRequestWithFallback('post', endpoint, data, config),
    put: (endpoint, data, config) => makeRequestWithFallback('put', endpoint, data, config),
    patch: (endpoint, data, config) => makeRequestWithFallback('patch', endpoint, data, config),
    delete: (endpoint, config) => makeRequestWithFallback('delete', endpoint, config)
  };
};

export default {
  createVersionedApi,
  createAuthenticatedApi,
  useVersionedApi,
  createLegacyApi,
  createMigrationApi,
  apiV1,
  apiV2
};
